import {
  CheckOutlined,
  ExclamationOutlined,
  LockOutlined,
  SafetyCertificateFilled,
} from '@ant-design/icons';
import { Spin } from 'antd';
import { useLanguage } from 'hooks/Language/useLanguage';
import { IKycDocumentState } from 'utils/kycStatus';
import { StepStatus } from 'hooks/useKycUpload';

interface Props {
  documents: IKycDocumentState[];
  activeKey: string;
  statuses: Record<string, StepStatus>;
  done: number;
  total: number;
  onSelect: (key: string) => void;
  onLater: () => void;
}

const KycStepRail: React.FC<Props> = ({
  documents,
  activeKey,
  statuses,
  done,
  total,
  onSelect,
  onLater,
}) => {
  const { translate } = useLanguage();

  return (
    <aside className="kyc-rail">
      <div className="kyc-rail__brand">
        <span className="kyc-rail__badge">
          <SafetyCertificateFilled />
        </span>
        <div>
          <h2>{translate('Verify your identity')}</h2>
          <p>
            {translate(
              'A few photos and your account is ready to use.',
            )}
          </p>
        </div>
      </div>

      <div className="kyc-rail__progress">
        <span>
          {done}/{total} {translate('required')}
        </span>
        <div className="kyc-rail__bar">
          <span
            style={{ width: `${Math.round((done / total) * 100)}%` }}
          />
        </div>
      </div>

      <ol className="kyc-rail__steps">
        {documents.map((doc, index) => {
          const status = statuses[doc.key];
          const isDone = doc.presence === 'present';
          const state =
            status === 'uploading'
              ? 'busy'
              : status === 'error'
                ? 'error'
                : isDone
                  ? 'done'
                  : 'todo';

          return (
            <li key={doc.key}>
              <button
                type="button"
                className={`kyc-rail__step kyc-rail__step--${state}${
                  doc.key === activeKey ? ' is-active' : ''
                }`}
                aria-current={
                  doc.key === activeKey ? 'step' : undefined
                }
                onClick={() => onSelect(doc.key)}
              >
                <span className="kyc-rail__dot">
                  {state === 'busy' ? (
                    <Spin size="small" />
                  ) : state === 'done' ? (
                    <CheckOutlined />
                  ) : state === 'error' ? (
                    <ExclamationOutlined />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="kyc-rail__label">
                  {translate(doc.label)}
                  <small>
                    {state === 'done'
                      ? translate('Received')
                      : doc.required
                        ? translate('Required')
                        : translate('Optional')}
                  </small>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <footer className="kyc-rail__foot">
        <span>
          <LockOutlined />
          {translate('Encrypted. Only the AGB team sees it.')}
        </span>
        <button type="button" onClick={onLater}>
          {translate('Remind me later')}
        </button>
      </footer>
    </aside>
  );
};

export default KycStepRail;
